import React, {
  useEffect,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import Style from './style';
import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Save,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import logo from '../CardView/logo';
import { Interfaces, } from '../../../config';
import { useDSCalls, } from '../../../hooks';
import LoadingComponent from '../../Loading';
import {
  StyledButton,
  StyledTextField,
} from '../../Reusable';


type Params = {
  oldName: string;
};

function MSSQL() {
  const classes = Style();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState,] = useState<Interfaces.JDBCDataSource>({
    id: '',
    name: '',
    type: 'JDBC',
    uri: 'jdbc:sqlserver://${host}:${port};databaseName=${dbName}',
    attributes: null,
    databaseName: '',
    databaseHost: '',
    databasePort: '1433',
    databaseTls: false,
    description: '',
    driverClassName: 'com.microsoft.sqlserver.jdbc.SQLServerDriver',
    driverFilePath: '',
    driverFileType: '',
    driverName: 'sqlserver',
    password: '',
    username: '',
  });
  const [showPassword, setShowPassword,] = React.useState<boolean>(false);
  const { oldName, } = useParams<Params>();
  const [isInEditMode, setIsInEditMode,] = useState(false);

  const { loading: loadingDS,
    editDataSource,
    addDataSource,
    testDataSource,
    dataSourceToEdit: dataSourceToEditHook,
    loadingEdit,
    errorEdit,
    loadingTest,
    errorTest,
    uploadDriverAPI,
    fetchDataSource,
    testNewDataSource,
    testExistingDataSource,
    setTestDataSource, } = useDSCalls();

  const dataSourceToEdit = dataSourceToEditHook as Interfaces.JDBCDataSource | null;
  const [loadingDriver, setLoadingDriver,] = useState(false);

  useEffect(() => {
    setTestDataSource(null);
    const editMode = location.pathname.includes('/edit');
    setIsInEditMode(editMode);
    if (editMode && dataSourceToEdit == null && oldName) {
      fetchDataSource(oldName);
    }
    if (editMode && dataSourceToEdit != null) {
      setState({
        ...state,
        id: dataSourceToEdit?.id,
        uri: dataSourceToEdit?.uri,
        name: dataSourceToEdit?.name,
        description: dataSourceToEdit?.description,
        type: dataSourceToEdit?.type,
        driverName: dataSourceToEdit?.driverName,
        driverClassName: dataSourceToEdit?.driverClassName,
        driverFilePath: dataSourceToEdit?.driverFilePath,
        driverFileType: dataSourceToEdit?.driverFileType,
        databaseHost: dataSourceToEdit?.databaseHost,
        databaseName: dataSourceToEdit?.databaseName,
        databasePort: dataSourceToEdit?.databasePort,
        databaseTls: dataSourceToEdit?.databaseTls,
        username: dataSourceToEdit?.username,
        password: null,
      });
    }
  }, [dataSourceToEdit,]);

  const createUri = (name: string, dbName: string, host: string, port: string) => {
    const uri = `jdbc:${name}://${host}:${port};databaseName=${dbName}`;
    return `${uri}`;
  };

  const setUri = (name: string, value: string) => {
    switch (name) {
    case 'driverName':
      return createUri(value, state.databaseName, state.databaseHost, state.databasePort);
    case 'databaseName':
      return createUri(state.driverName, value, state.databaseHost, state.databasePort);
    case 'databaseHost':
      return createUri(state.driverName, state.databaseName, value, state.databasePort);
    case 'databasePort':
      return createUri(state.driverName, state.databaseName, state.databaseHost, value);
    case 'uri':
      return value;
    default:
      return state.uri;
    }
  };

  const handleChange = () => (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;
    setState({
      ...state,
      [name]: value,
      uri: setUri(name, value),
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const onTestConnection = () => {
    isInEditMode ? testExistingDataSource(state) : testNewDataSource(state);
  };

  const driverUpload = (files: any) => {
    setLoadingDriver(true);
    if (files.length > 0) {
      uploadDriverAPI(files[0], state.driverClassName, (driverFilePath) => {
        setState({
          ...state,
          driverFilePath,
        });
        setLoadingDriver(false);
      });
      return;
    }
    setLoadingDriver(false);
  };

  const postSubmitAction = () => {navigate('/data-sources');};

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (location.pathname.includes('/edit') && oldName) {
      editDataSource(state, oldName, postSubmitAction);
      return;
    }
    addDataSource(state, postSubmitAction);
  };

  const handleTestSuccess = () => {
    if (loadingTest) {
      return <CircularProgress
        color="inherit"
        style={{
          width: 11,
          height: 11,
        }}
      />;
    }
    if (testDataSource && !errorTest) {
      return <FiberManualRecordIcon style={{ fill: 'green', }} />;
    } else if (
      !testDataSource &&
      !errorTest
    ) {
      return <FiberManualRecordIcon style={{ fill: 'grey', }} />;
    } else if (
      errorTest
    ) {
      return <FiberManualRecordIcon style={{ fill: 'red', }} />;
    }
  };

  if (loadingEdit) {
    return <LoadingComponent message={'Loading data source'}/>;
  }

  if (errorEdit) {
    return (
      <p>{errorEdit}</p>
    );
  }

  return (
    <div className={classes.root}>
      <Grid container justifyContent='center' >
        <img alt="jdbc" src={logo.images.mssql} className={classes.img} />
      </Grid>
      <Grid container justifyContent='center' spacing={3}>
        <form name="addJdbcForm" onSubmit={handleSubmit} style={{ width: '78%', }}>
          <Grid item>
            <StyledTextField
              id="name"
              label="Connection Name"
              variant="outlined"
              name="name"
              onChange={handleChange()}
              fullWidth
              size="small"
              value={state.name}
              required
            />
            <StyledTextField
              id="description"
              label="Description"
              multiline
              rows={4}
              variant="outlined"
              name="description"
              onChange={handleChange()}
              fullWidth
              value={state.description}
              size="small"
            />
            <Grid container spacing={3}>
              <Grid item xs={10}>
                <StyledTextField
                  id="database.host"
                  label="Hostname"
                  variant="outlined"
                  name="databaseHost"
                  onChange={handleChange()}
                  fullWidth
                  size="small"
                  value={state.databaseHost}
                  required
                />
              </Grid>
              <Grid item xs={2}>
                <StyledTextField
                  id="database.port"
                  label="Port"
                  variant="outlined"
                  name="databasePort"
                  onChange={handleChange()}
                  fullWidth
                  size="small"
                  value={state.databasePort}
                  required
                />
              </Grid>
            </Grid>
            <StyledTextField
              id="database.name"
              label="Database name"
              variant="outlined"
              name="databaseName"
              onChange={handleChange()}
              fullWidth
              size="small"
              value={state.databaseName}
              required
            />
            <StyledTextField
              id="credentials.username"
              label="Username"
              variant="outlined"
              name="username"
              onChange={handleChange()}
              fullWidth
              size="small"
              value={state.username}
            />
            <StyledTextField
              type={showPassword ? 'text' : 'password'}
              value={state.password}
              placeholder={state.password === null ? '(Password protected)' : 'Password'}
              onFocus={() => setState({
                ...state,
                password: '',
              })}
              size={'small'}
              fullWidth
              label={state.password === null ? '(Password protected)' : 'Password'}
              onChange={handleChange()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              name="password"
            />
            <Grid container spacing={3}>
              <Grid item xs={10}>
                <StyledTextField
                  className={classes.paddingBox}
                  id="outlined-basic"
                  label="JDBC Driver (Class Name)"
                  variant="outlined"
                  name="driverClassName"
                  onChange={handleChange()}
                  fullWidth
                  size="small"
                  value={state.driverClassName}
                  required
                />
              </Grid>
              <Grid item xs={2}>
                <input
                  accept=".jar"
                  className={classes.input}
                  id="input-file"
                  onChange={(e: any) => {
                    driverUpload(e.target.files);
                    e.target.value = '';
                  }}
                  type="file"
                  disabled={!state.driverClassName || state.driverClassName === ''}
                />
                <label htmlFor="input-file">
                  <StyledButton
                    variant="contained"
                    color="primary"
                    component={'span'}
                    startIcon={
                      loadingDriver ?
                        <CircularProgress
                          color="inherit"
                          style={{
                            width: 16,
                            height: 11,
                          }} />
                        : <CloudUploadIcon />
                    }
                    size="large"
                    fullWidth
                    disabled={!state.driverClassName || state.driverClassName === ''}
                  >
                    Upload
                  </StyledButton>
                </label>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={10}>
                <StyledTextField
                  id="driver.uri"
                  placeholder="jdbc:sqlserver://{host}[:{port}][;databaseName={database}]"
                  label="JDBC Connection String"
                  variant="outlined"
                  name="uri"
                  onChange={handleChange()}
                  fullWidth
                  size="small"
                  value={state.uri}
                  required
                />
              </Grid>
              <Grid item xs={2}>
                <StyledButton
                  variant="contained"
                  color="primary"
                  startIcon={handleTestSuccess()}
                  size="large"
                  fullWidth
                  onClick={() => {
                    onTestConnection();
                  }}
                >
                  Test
                </StyledButton>
              </Grid>
            </Grid>
            {errorTest && (
              <Typography variant="subtitle2" style={{ color: 'red', }}>
                {errorTest}
              </Typography>
            )}

            <StyledButton
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              style={{ marginLeft: 0, }}
              disabled={loadingDriver || loadingDriver}
              startIcon={loadingDS || loadingDriver ?
                <CircularProgress
                  color="inherit"
                  style={{
                    width: 16,
                    height: 11,
                  }} />
                :
                <Save />}
            >
              Save
            </StyledButton>
            <StyledButton
              variant="outlined"
              size="large"
              onClick={() => {
                navigate(-1);
              }}
            >
              Cancel
            </StyledButton>
          </Grid>
        </form>
      </Grid>
    </div>
  );
}

export default MSSQL;
